import {memberRoleActsForTrustee} from '@sollapay/domain/rules';
import {TrustAccountMemberRole} from '@sollapay/enums';
import {normalizeEmail} from '@sollapay/utils';
import {useCallback, useMemo, useState} from 'react';

import {canHoldRole, getRosterPeople, memberDisplayName} from '@/features/trust-accounts/members';

import {addChipEmails, classifyShareEntry, getShareRefusals, getShareSuggestions} from '../utils';

import type {ShareChip, ShareRefusal} from '../utils';
import type {
  TrustAccountGuestDTO,
  TrustAccountMemberCandidateDTO,
  TrustAccountMembersDTO
} from '@sollapay/types';
import type {QueryStatus} from '@tanstack/react-query';

type Candidate = TrustAccountMemberCandidateDTO;
type MemberChip = Extract<ShareChip, {kind: 'member'}>;
type SendOutcome = Awaited<ReturnType<typeof sendShareChips>>;

const NO_REFUSALS: ReadonlyMap<string, ShareRefusal> = new Map();
const NO_CHIPS: ReadonlyMap<string, ShareChip> = new Map();

/**
 * `candidatesStatus` holds the send until the firm users are known, so none goes out as a guest.
 * `onMembersAdded` runs once after a send's member adds, so the team refreshes once, not per person.
 */
export interface ShareInviteFormParams {
  roster: TrustAccountMembersDTO;
  guests: TrustAccountGuestDTO[];
  candidates: Candidate[];
  candidatesStatus?: QueryStatus;
  onInviteGuests: (emails: string[]) => Promise<void>;
  onAddMember: (person: Candidate, role: TrustAccountMemberRole) => Promise<boolean>;
  onMembersAdded?: () => Promise<void> | void;
}

/** The share field's chips until sent: firm users join as members in the role, anyone else as a guest. */
export function useShareInviteForm(params: ShareInviteFormParams) {
  const [draft, setDraft] = useState('');
  const [chosenRole, setChosenRole] = useState(TrustAccountMemberRole.VIEWER);
  const lookups = useShareLookups(params);
  const list = useShareChipList({role: chosenRole, draft, ...lookups});
  const {role, chips, blocked} = list;
  const sending = useShareSending({params, role, list});
  const sendCount = chips.length + (isTyped(draft) ? 1 : 0);
  // Until the firm users load, a typed address can't be told from a guest's, so nothing is offered.
  const isLoadingSuggestions = isTyped(draft) && params.candidatesStatus === 'pending';
  const operators = getNewOperators(chips, role);
  const consent = usePowerOfAttorneyConsent(operators);
  const {changeChips, submit} = useShareSubmit({
    list,
    sending,
    draft,
    setDraft,
    consent,
    operators
  });

  return {
    field: {
      chips,
      draft,
      suggestions: isLoadingSuggestions ? [] : list.suggestions,
      isLoadingSuggestions,
      hasError: blocked.length > 0 || sending.status.hasFailed,
      onDraftChange: setDraft,
      onChipsChange: changeChips,
      onSubmit: () => void submit()
    },
    roleDetails: {
      role,
      isOperatorForced: list.isOperatorForced,
      hasFirmChips: list.hasFirmChips,
      hasGuestChips: list.hasGuestChips,
      onRoleChange: (next: TrustAccountMemberRole) => {
        setChosenRole(next);
        sending.reset();
      },
      powerOfAttorney: consent.details
    },
    blocked,
    isAwaitingPowerOfAttorney: consent.isAwaiting,
    sendCount,
    submit,
    ...sending.status
  };
}

/** Sends the chips and the typed draft, unless the consent is withdrawn or doesn't name everyone yet. */
function useShareSubmit(params: {
  list: ShareChipList;
  sending: ReturnType<typeof useShareSending>;
  draft: string;
  setDraft: (draft: string) => void;
  consent: ReturnType<typeof usePowerOfAttorneyConsent>;
  operators: Candidate[];
}) {
  const {list, sending, draft, setDraft, consent, operators} = params;

  const changeChips = (next: string[]) => {
    list.replaceEmails(next);
    sending.reset();
  };

  const submit = async () => {
    if (!sending.canStart || consent.isAwaiting) return;
    const all = isTyped(draft) ? addChipEmails(list.emails, [draft]) : list.emails;
    changeChips(all);
    setDraft('');
    const toSend = all.map(list.chipOf);
    const hasUnconsentedOperator = getNewOperators(toSend, list.role).length > operators.length;
    // A typed firm user who'd become an operator isn't named in the consent yet: show it first.
    if (hasUnconsentedOperator) return;
    await sending.start(toSend);
  };

  return {changeChips, submit};
}

function isTyped(draft: string): boolean {
  return draft.trim() !== '';
}

function useShareLookups({roster, guests, candidates}: ShareInviteFormParams) {
  const accessEmails = useMemo(
    () => new Set([...getRosterPeople(roster), ...guests].map(({email}) => normalizeEmail(email))),
    [roster, guests]
  );
  const candidatesByEmail = useMemo(
    () => new Map(candidates.map(person => [normalizeEmail(person.email), person])),
    [candidates]
  );
  return {accessEmails, candidatesByEmail};
}

type ShareChipList = ReturnType<typeof useShareChipList>;

/**
 * The chipped emails and their kinds; chips in flight keep theirs, so an access refetch can't flip them.
 * Chipping a firm user who can only operate makes the whole send operate.
 */
function useShareChipList(
  params: Omit<Parameters<typeof classifyShareEntry>[0], 'email'> & {draft: string}
) {
  const {candidatesByEmail, accessEmails} = params;
  const [emails, setEmails] = useState<string[]>([]);
  const [refusals, setRefusals] = useState(NO_REFUSALS);
  const [inFlight, setInFlight] = useState(NO_CHIPS);
  const isOperatorForced = emails.some(email => canOnlyOperate(candidatesByEmail.get(email)));
  const role = isOperatorForced ? TrustAccountMemberRole.OPERATOR : params.role;

  const chipOf = useCallback(
    (email: string): ShareChip => {
      const sending = inFlight.get(email);
      if (sending) return sending;
      const refusal = refusals.get(email);
      // A guest refused as a firm user turns into a member chip once the firm users have loaded.
      return refusal && !(refusal === 'firm' && candidatesByEmail.has(email))
        ? {email, kind: 'problem', problem: refusal}
        : classifyShareEntry({email, role, candidatesByEmail, accessEmails});
    },
    [inFlight, refusals, role, candidatesByEmail, accessEmails]
  );
  const chips = useMemo(() => emails.map(chipOf), [emails, chipOf]);
  const chippedEmails = useMemo(() => new Set(emails), [emails]);
  const suggestions = useShareSuggestions({...params, role, chippedEmails});

  const replaceEmails = (next: string[]) => {
    setEmails(next);
    setRefusals(current => new Map([...current].filter(([email]) => next.includes(email))));
  };

  const finishSending = (sent: ShareChip[], {unsent, refusals: refused}: SendOutcome) => {
    const sentEmails = new Set(sent.map(chip => chip.email));
    setInFlight(NO_CHIPS);
    setEmails(current => current.filter(email => unsent.has(email) || !sentEmails.has(email)));
    if (refused) setRefusals(current => new Map([...current, ...refused]));
  };

  return {
    emails,
    role,
    isOperatorForced,
    suggestions,
    chips,
    blocked: chips.filter(isProblemChip),
    hasFirmChips: emails.some(email => candidatesByEmail.has(email)),
    hasGuestChips: chips.some(chip => chip.kind === 'guest'),
    chipOf,
    replaceEmails,
    startSending: (sent: ShareChip[]) => setInFlight(new Map(sent.map(chip => [chip.email, chip]))),
    finishSending
  };
}

function useShareSuggestions(params: Parameters<typeof getShareSuggestions>[0]) {
  const {draft, role, chippedEmails, candidatesByEmail, accessEmails} = params;
  return useMemo(
    () => getShareSuggestions({draft, role, chippedEmails, candidatesByEmail, accessEmails}),
    [draft, role, chippedEmails, candidatesByEmail, accessEmails]
  );
}

/** The new operators' power of attorney: shown while there are any, granted each time it appears. */
function usePowerOfAttorneyConsent(operators: Candidate[]) {
  const [isGranted, setIsGranted] = useState(true);
  if (!operators.length && !isGranted) setIsGranted(true);
  return {
    isAwaiting: operators.length > 0 && !isGranted,
    details: operators.length
      ? {operatorNames: operators.map(memberDisplayName), isGranted, onGrantedChange: setIsGranted}
      : null
  };
}

interface ShareSendingParams {
  params: ShareInviteFormParams;
  role: TrustAccountMemberRole;
  list: ShareChipList;
}

function useShareSending({params, role, list}: ShareSendingParams) {
  const {candidatesStatus = 'success'} = params;
  const [hasFailed, setHasFailed] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const start = async (chips: ShareChip[]) => {
    if (!chips.length || chips.some(isProblemChip)) return;
    setIsSending(true);
    list.startSending(chips);
    const outcome = await sendShareChips({...params, chips, role});
    list.finishSending(chips, outcome);
    setIsSending(false);
    setHasFailed(outcome.hasFailed);
  };

  return {
    canStart: !isSending && candidatesStatus === 'success',
    start,
    reset: () => setHasFailed(false),
    status: {isSending, hasFailed}
  };
}

/** The firm users a send would make operators, each needing the trustee's power of attorney. */
function getNewOperators(chips: ShareChip[], role: TrustAccountMemberRole): Candidate[] {
  return memberRoleActsForTrustee(role) ? chips.filter(isMemberChip).map(chip => chip.person) : [];
}

function canOnlyOperate(person: Candidate | undefined): boolean {
  return (
    !!person &&
    canHoldRole(person, TrustAccountMemberRole.OPERATOR) &&
    !canHoldRole(person, TrustAccountMemberRole.VIEWER)
  );
}

function isMemberChip(chip: ShareChip): chip is MemberChip {
  return chip.kind === 'member';
}

function isProblemChip(chip: ShareChip): boolean {
  return chip.kind === 'problem';
}

/** The guest invite and the member adds go out together; whatever didn't go through stays to retry. */
async function sendShareChips({
  chips,
  role,
  onInviteGuests,
  onAddMember,
  onMembersAdded
}: ShareInviteFormParams & {chips: ShareChip[]; role: TrustAccountMemberRole}) {
  const guestEmails = chips.filter(chip => chip.kind === 'guest').map(chip => chip.email);
  const [guests, unaddedMembers] = await Promise.all([
    inviteGuests(guestEmails, onInviteGuests),
    addMembers(chips.filter(isMemberChip), {role, onAddMember, onMembersAdded})
  ]);
  return {
    unsent: new Set([...guests.unsent, ...unaddedMembers]),
    refusals: guests.refusals,
    hasFailed: guests.hasFailed || unaddedMembers.length > 0
  };
}

async function inviteGuests(
  emails: string[],
  onInviteGuests: ShareInviteFormParams['onInviteGuests']
): Promise<{unsent: string[]; refusals: Map<string, ShareRefusal> | null; hasFailed: boolean}> {
  if (!emails.length) return {unsent: [], refusals: null, hasFailed: false};
  try {
    await onInviteGuests(emails);
    return {unsent: [], refusals: null, hasFailed: false};
  } catch (error) {
    const refusals = getShareRefusals(error);
    return {unsent: emails, refusals, hasFailed: !refusals};
  }
}

/** One at a time: each add gets its own undo toast, and the server checks each member's version. */
async function addMembers(
  members: MemberChip[],
  {
    role,
    onAddMember,
    onMembersAdded
  }: Pick<ShareInviteFormParams, 'onAddMember' | 'onMembersAdded'> & {role: TrustAccountMemberRole}
): Promise<string[]> {
  const unadded: string[] = [];
  for (const {email, person} of members) {
    if (!(await onAddMember(person, role))) unadded.push(email);
  }
  if (members.length) await onMembersAdded?.();
  return unadded;
}
